import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactDialog = ({ triggerClassName = "", label = "Contact Us", defaultEmail = "", open: openProp, onOpenChange, hideTrigger = false }) => {
  const [openState, setOpenState] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = (v) => { if (isControlled) onOpenChange?.(v); else setOpenState(v); };
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: defaultEmail, subject: "", message: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send message");
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: defaultEmail, subject: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={triggerClassName || "inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition"}
          data-testid="contact-open-btn"
        >
          <Mail className="w-4 h-4" /> {label}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>Contact Us</DialogTitle>
            <DialogDescription>Questions or feedback? Send us a message and we'll get back to you.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input id="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Your name" data-testid="contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder="you@example.com" data-testid="contact-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-subject">Subject</Label>
              <Input id="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)}
                placeholder="How can we help?" data-testid="contact-subject" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-message">Message</Label>
              <Textarea id="contact-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
                placeholder="Write your message..." data-testid="contact-message" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="contact-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : "Send Message"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactDialog;
